import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, Modal, Select, Text, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";

import { RequestPayoutPayload, useRequestPayout } from "@hooks/mutations/earnings/useRequestPayout";

interface RequestPayoutModalProps {
  opened: boolean;
  onClose: () => void;
  availableAmount: number;
}

export const RequestPayoutModal = ({
  opened,
  onClose,
  availableAmount,
}: RequestPayoutModalProps) => {
  const { t, i18n } = useTranslation();

  const { mutate: requestPayout, isLoading } = useRequestPayout();

  const form = useForm({
    initialValues: {
      payoutMethod: "bank_transfer",
      notes: "",
      bankName: "",
      bankAccountNumber: "",
      mobileMoneyNumber: "",
    },
    validate: {
      payoutMethod: (value) => (value.trim().length > 0 ? null : t("required-field")),
      bankName: (value, values) => 
        values.payoutMethod === "bank_transfer" && !value ? t("required-field") : null,
      bankAccountNumber: (value, values) => 
        values.payoutMethod === "bank_transfer" && !value ? t("required-field") : null,
      mobileMoneyNumber: (value, values) => 
        values.payoutMethod === "mobile_money" && !value ? t("required-field") : null,
    },
  });

  // Reset form when modal closes or opens
  useEffect(() => {
    if (opened) {
      form.reset();
    }
  }, [opened]);

  const handleSubmit = (values: typeof form.values) => {
    const payload: RequestPayoutPayload = {
      payout_type: "commission",
      payout_method: values.payoutMethod,
      notes: values.notes,
      bank_name: values.payoutMethod === "bank_transfer" ? values.bankName : undefined,
      bank_account_number: values.payoutMethod === "bank_transfer" ? values.bankAccountNumber : undefined,
      mobile_money_number: values.payoutMethod === "mobile_money" ? values.mobileMoneyNumber : undefined,
    };

    requestPayout(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{t("request-payout")}</Text>}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group mb="xl" gap="xs">
          <Text size="sm" c="dimmed">{t("available-amount")}:</Text>
          <Text size="sm" fw={600} c="green.7">
            {Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "CFA",
            }).format(availableAmount)}
          </Text>
        </Group>

        <Select
          label={t("payout-method")}
          placeholder={t("select")}
          data={[
            { label: "Bank Transfer", value: "bank_transfer" },
            { label: "Mobile Money", value: "mobile_money" },
          ]}
          mb="md"
          withAsterisk
          {...form.getInputProps("payoutMethod")}
        />

        {form.values.payoutMethod === "bank_transfer" && (
          <>
            <TextInput
              label={t("bank-name")}
              placeholder={t("enter-bank-name")}
              mb="md"
              withAsterisk
              {...form.getInputProps("bankName")}
            />
            <TextInput
              label={t("account-number")}
              placeholder={t("enter-account-number")}
              mb="md"
              withAsterisk
              {...form.getInputProps("bankAccountNumber")}
            />
          </>
        )}

        {form.values.payoutMethod === "mobile_money" && (
          <TextInput
            label={t("mobile-money-number")}
            placeholder={t("enter-mobile-money-number")}
            mb="md"
            withAsterisk
            {...form.getInputProps("mobileMoneyNumber")}
          />
        )}

        <Textarea
          label={t("notes")}
          placeholder={t("type-notes-optional")}
          minRows={3}
          mb="xl"
          {...form.getInputProps("notes")}
        />

        <Group justify="flex-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button type="submit" loading={isLoading} disabled={availableAmount <= 0}>
            {t("submit-request")}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
